import { useState, useEffect } from "react";
import "../../../shared/css/ElementoPlanoOrcamentoAvaliacao.scss";
import "./ElementoOrcamentoDireto.scss";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import InputLabel from "@mui/material/InputLabel";

import DeleteIcon from "@mui/icons-material/Delete";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      color: "#0c53fb",
    },
  },
};

const facesDentes = ["D", "M", "O/I", "P/L", "V"];

function ElementoOrcamentoDireto({
  id,
  tratamentos,
  tratamentosAutocomplete,
  dentes,
  show,
  podeRemover,
  // Dados iniciais
  tratamentoInicial,
  dentesTratadosIniciais,
  facesIniciais,
  // Cobertura
  tipoCobertura,
  instituicaoSelecionada,
  modalidadePagamento,
  // Handlers
  onTratamentoChange,
  onDentesChange,
  onFacesChange,
  onRemove,
  onDuplicate,
}) {
  const [dentes_, setDentes] = useState(dentesTratadosIniciais || []);
  const [faces, setFaces] = useState(facesIniciais || []);
  const [showFaces, setShowFaces] = useState(false);
  const [showDentes, setShowDentes] = useState(true);

  const [tratamento, setTratamento] = useState(null);
  const [tratamentoAutocomplete, setTratamentoAutocomplete] = useState(null);
  const [listaDentes, setListaDentes] = useState(dentes);
  const [erroDentes, setErroDentes] = useState(false);
  const [erroFaces, setErroFaces] = useState(false);

  // Inicializar tratamento
  useEffect(() => {
    if (tratamentoInicial && tratamentos.length > 0) {
      const tratamentoCompleto = tratamentos.find(
        (t) => t.id === tratamentoInicial || t._id === tratamentoInicial
      );

      if (tratamentoCompleto) {
        setTratamento(tratamentoCompleto);
        setTratamentoAutocomplete(
          tratamentosAutocomplete.find((t) => t.id === tratamentoCompleto.id)
        );

        // Configurar lista de dentes
        if (tratamentoCompleto.dentesEspecificos) {
          setListaDentes(tratamentoCompleto.dentesEspecificos.split(", "));
        } else {
          setListaDentes(dentes);
        }

        // Configurar visibilidade
        setShowDentes(tratamentoCompleto.aceitaDentes);
        setShowFaces(
          tratamentoCompleto.aceitaFaces &&
            !["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(
              tratamentoCompleto.dentesEspecificos
            )
        );
      }
    }
  }, [tratamentoInicial, tratamentos, tratamentosAutocomplete, dentes]);

  // Sincronizar dentes quando props mudam
  useEffect(() => {
    if (dentesTratadosIniciais && dentesTratadosIniciais.length > 0) {
      setDentes(dentesTratadosIniciais);
    }
  }, [dentesTratadosIniciais]);

  // Sincronizar faces quando props mudam
  useEffect(() => {
    if (facesIniciais) {
      setFaces(facesIniciais);
    }
  }, [facesIniciais]);

  const dentesChangeHandler = (event) => {
    const {
      target: { value },
    } = event;

    let erro = "";
    if (tratamento?.multiDentes && value.length !== tratamento.qtdDentes) {
      erro = `O tratamento ${tratamento.designacao} exige que selecione ${tratamento.qtdDentes} dentes. Foram selecionados ${value.length}.`;
      setErroDentes(true);
    } else {
      setErroDentes(false);
    }

    // Verificar arcadas
    if (
      value.includes("Arcada Inferior") ||
      value.includes("Arcada Superior") ||
      value.includes("Arcadas") ||
      !tratamento?.aceitaFaces
    ) {
      setFaces([]);
      setShowFaces(false);
      onFacesChange(id, []);
    } else {
      setShowFaces(tratamento?.aceitaFaces || false);
    }

    // Tratar seleção de arcadas
    if (
      value.includes("Arcada Inferior") ||
      value.includes("Arcada Superior") ||
      value.includes("Arcadas")
    ) {
      if (value.includes("Arcadas")) {
        setDentes(["Arcadas"]);
        onDentesChange(id, ["Arcadas"], erro);
      } else {
        const apenasArcadas = value.filter(
          (v) => v === "Arcada Inferior" || v === "Arcada Superior"
        );
        setDentes(apenasArcadas);
        onDentesChange(id, apenasArcadas, erro);
      }
    } else {
      const newDentes = typeof value === "string" ? value.split(",") : value;
      setDentes(newDentes);
      onDentesChange(id, newDentes, erro);
    }
  };

  const facesChangeHandler = (event) => {
    const {
      target: { value },
    } = event;

    let erro = "";
    if (tratamento?.qtdFaces !== 5 && value.length !== tratamento?.qtdFaces) {
      erro = `O tratamento ${tratamento.designacao} exige que selecione ${tratamento.qtdFaces} faces. Foram selecionadas ${value.length}.`;
      setErroFaces(true);
    } else {
      setErroFaces(false);
    }

    const newFaces = typeof value === "string" ? value.split(",") : value;
    setFaces(newFaces);
    onFacesChange(id, newFaces, erro);
  };

  const tratamentoChangeHandler = (e, value) => {
    if (!value) return;

    const tratamentoFiltrado = tratamentos.find((tr) => tr.id === value.id);
    if (!tratamentoFiltrado) return;

    // Atualizar lista de dentes
    if (tratamentoFiltrado.dentesEspecificos) {
      const dentesEspecificosArray =
        tratamentoFiltrado.dentesEspecificos.split(", ");
      setListaDentes(dentesEspecificosArray);

      if (tratamentoFiltrado.dentesEspecificos.split(",").length === 1) {
        const novoDente = tratamentoFiltrado.dentesEspecificos.split(",");
        setDentes(novoDente);
        onDentesChange(id, novoDente, "");
      } else if (
        !dentes_.every((d) =>
          tratamentoFiltrado.dentesEspecificos.split(",").includes(d)
        )
      ) {
        setDentes([]);
        onDentesChange(id, [], "");
      }
    } else {
      setListaDentes(dentes);
    }

    // Configurar faces
    const deveEsconderFaces =
      !tratamentoFiltrado.aceitaFaces ||
      dentes_.some((d) =>
        ["Arcada Inferior", "Arcada Superior", "Arcadas"].includes(d)
      ) ||
      ["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(
        tratamentoFiltrado.dentesEspecificos
      );

    if (deveEsconderFaces) {
      setFaces([]);
      onFacesChange(id, []);
      setShowFaces(false);
    } else {
      setShowFaces(true);
    }

    // Configurar dentes
    if (!tratamentoFiltrado.aceitaDentes) {
      setDentes([]);
      onDentesChange(id, []);
      setShowDentes(false);
    } else {
      setShowDentes(true);
    }

    // Verificar erros
    let erro = "";
    if (
      tratamentoFiltrado.multiDentes &&
      dentes_.length !== tratamentoFiltrado.qtdDentes
    ) {
      erro = `O tratamento ${tratamentoFiltrado.designacao} exige ${tratamentoFiltrado.qtdDentes} dentes. ${dentes_.length} selecionados.`;
      setErroDentes(true);
    } else {
      setErroDentes(false);
    }

    if (faces.length > tratamentoFiltrado.qtdFaces) {
      erro = `O tratamento ${tratamentoFiltrado.designacao} exige no máximo ${tratamentoFiltrado.qtdFaces} faces.`;
      setErroFaces(true);
    } else {
      setErroFaces(false);
    }

    setTratamento(tratamentoFiltrado);
    setTratamentoAutocomplete(value);
    onTratamentoChange(id, tratamentoFiltrado, erro);
  };

  // Calcular preços baseado na cobertura
  const getPrecos = () => {
    if (!tratamento) {
      return {
        valorUnitario: 0,
        comparticipacao: 0,
        valorBeneficiario: 0,
      };
    }

    if (tipoCobertura === "PARTICULAR" || !instituicaoSelecionada) {
      return {
        valorUnitario: tratamento.preco_particular,
        comparticipacao: 0,
        valorBeneficiario: tratamento.preco_particular,
      };
    }

    const cobertura = tratamento.precosPorInstituicao?.find(
      (p) => p.instituicao === instituicaoSelecionada._id
    );

    if (!cobertura?.coberto) {
      return {
        valorUnitario: tratamento.preco_particular,
        comparticipacao: 0,
        valorBeneficiario: tratamento.preco_particular,
        naoCoberto: true,
      };
    }

    return {
      valorUnitario: cobertura.preco_segurado,
      comparticipacao: cobertura.comparticipacao,
      valorBeneficiario:
        modalidadePagamento === "DIRETO"
          ? cobertura.preco_beneficiario
          : cobertura.preco_segurado,
      coberto: true,
    };
  };

  const precos = getPrecos();

  // Render option para autocomplete
  const renderOption = (props, option) => (
    <Box
      component="span"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: "8px",
      }}
      {...props}
    >
      <Typography variant="subtitle1">{option.label}</Typography>
      <Typography variant="body2" color="textSecondary">
        {option.categoria}
      </Typography>
    </Box>
  );

  // Filtro personalizado para autocomplete
  const filterOptions = (options, { inputValue }) => {
    const normalizeText = (text) => {
      return text
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    };

    const searchTerms = normalizeText(inputValue).split(" ").filter(Boolean);

    return options.filter((option) => {
      const optionText = normalizeText(`${option.label} - ${option.categoria}`);
      return searchTerms.every((term) => optionText.includes(term));
    });
  };

  if (!show) return null;

  return (
    <div
      className="orcamento-direto__elemento"
      style={{
        display: "grid",
        gridTemplateColumns:
          tipoCobertura === "PARTICULAR"
            ? "2fr 1fr 1fr 1fr 100px"
            : "3fr 1.5fr 1.5fr 1fr 1fr 1fr 100px",
        alignItems: "center",
      }}
    >
      {/* Seleção de Tratamento */}
      <FormControl sx={{ m: 1, minWidth: 250 }}>
        <Autocomplete
          id={`treatment-autocomplete-${id}`}
          value={tratamentoAutocomplete}
          onChange={tratamentoChangeHandler}
          options={tratamentosAutocomplete}
          getOptionLabel={(option) => `${option.label} - ${option.categoria}`}
          renderOption={renderOption}
          filterOptions={filterOptions}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tratamento"
              variant="outlined"
              fullWidth
            />
          )}
        />
      </FormControl>

      {/* Seleção de Dentes */}
      {showDentes && listaDentes && (
        <FormControl
          sx={{ m: 1, minWidth: 120 }}
          className={erroDentes ? "erroDentes" : ""}
        >
          <InputLabel id={`dentes-label-${id}`} sx={{ background: "#fff" }}>
            Dentes {dentes_.length > 0 ? `(${dentes_.length})` : ""}
          </InputLabel>
          <Select
            labelId={`dentes-label-${id}`}
            multiple
            value={dentes_}
            onChange={dentesChangeHandler}
            input={<OutlinedInput label="Dentes" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {listaDentes.map((dente) => (
              <MenuItem key={dente} value={dente}>
                <Checkbox checked={dentes_.indexOf(dente) > -1} />
                <ListItemText primary={dente} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Seleção de Faces */}
      {showFaces && (
        <FormControl
          sx={{ m: 1, minWidth: 100 }}
          className={erroFaces ? "erroDentes" : ""}
        >
          <InputLabel id={`faces-label-${id}`}>Faces</InputLabel>
          <Select
            labelId={`faces-label-${id}`}
            multiple
            value={faces}
            onChange={facesChangeHandler}
            input={<OutlinedInput label="Faces" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {facesDentes.map((face) => (
              <MenuItem key={face} value={face}>
                <Checkbox checked={faces.indexOf(face) > -1} />
                <ListItemText primary={face} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Espaçadores se não mostrar dentes ou faces */}
      {!showDentes && <span style={{ minWidth: 120 }}></span>}
      {!showFaces && <span style={{ minWidth: 100 }}></span>}

      {/* Coluna Val. Unitário */}
      <span className="orcamento-direto__preco">
        {Intl.NumberFormat("pt-BR").format(precos.valorUnitario)}$00
      </span>

      {/* Colunas de cobertura (apenas se não for PARTICULAR) */}
      {tipoCobertura !== "PARTICULAR" && instituicaoSelecionada && (
        <>
          <span className="orcamento-direto__preco">
            {precos.naoCoberto
              ? "-"
              : `${Intl.NumberFormat("pt-BR").format(
                  precos.comparticipacao
                )}$00`}
          </span>
          <span className="orcamento-direto__preco">
            {Intl.NumberFormat("pt-BR").format(precos.valorBeneficiario)}$00
          </span>
        </>
      )}

      {/* Botões de Ação */}
      <div className="elemento-avaliacao-btns-container">
        <Tooltip title="Duplicar">
          <button
            type="button"
            className="btn-acoes-avaliacao btn-adicionar"
            onClick={() =>
              onDuplicate({
                id,
                tratamento: tratamentoAutocomplete,
                dentesTratados: dentes_,
                faces,
              })
            }
          >
            <AddIcon className="mdi mdi-adicionar" />
            <AddCircleOutlineIcon className="mdi mdi-adicionar-empty" />
          </button>
        </Tooltip>
        {podeRemover && (
          <Tooltip title="Remover">
            <button
              type="button"
              className="btn-acoes-avaliacao btn-delete"
              onClick={() => onRemove(id)}
            >
              <DeleteIcon className="mdi mdi-delete" />
              <DeleteForeverIcon className="mdi mdi-delete-empty" />
            </button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default ElementoOrcamentoDireto;
